import React from "react";
import { Loader2, Lock, RefreshCw, CheckCircle2, XCircle, Clock } from "lucide-react";
import { useTrackingHttps } from "./useTrackingHttps";

function siteState(t) {
  if (t.https_ok) return { tone: "text-emerald-400", Icon: CheckCircle2, text: "HTTPS ready" };
  if (!t.eligible) return { tone: "text-zinc-500", Icon: XCircle, text: t.reason };
  if (t.https_error) return { tone: "text-red-400", Icon: XCircle, text: `cert: ${t.https_error}` };
  if (t.tls_status === "pending") return { tone: "text-amber-400", Icon: Clock, text: "cert pending" };
  if (!t.verified) return { tone: "text-amber-400", Icon: Clock, text: "CNAME not verified" };
  return { tone: "text-amber-400", Icon: Clock, text: t.associated === false ? "not linked to sending domain" : "HTTP only" };
}

function SiteLine({ t }) {
  const { tone, Icon, text } = siteState(t);
  return (
    <li data-testid={`tracking-https-site-${t.site_id}`} className="flex items-center gap-2 text-[11px] mono">
      <Icon size={11} className={tone} />
      <span className="text-zinc-300">{t.tracking_domain || t.domain || t.name}</span>
      <span className={tone}>{text}</span>
    </li>
  );
}

function ResultLine({ r }) {
  const ok = r.ok;
  const detail = r.error || r.pending || "HTTPS ready";
  return (
    <li data-testid={`tracking-https-result-${r.site_id}`} className="flex items-center gap-2 text-[11px] mono flex-wrap">
      {ok ? <CheckCircle2 size={11} className="text-emerald-400" /> : (r.error ? <XCircle size={11} className="text-red-400" /> : <Clock size={11} className="text-amber-400" />)}
      <span className="text-zinc-300">{r.tracking_domain || r.domain}</span>
      {r.dns && <span className="text-zinc-500">CNAME {r.dns}</span>}
      <span className={ok ? "text-emerald-400" : (r.error ? "text-red-400" : "text-amber-400")}>{detail}</span>
    </li>
  );
}

function JobView({ job }) {
  const results = job.results || [];
  const okCount = results.filter((r) => r.ok).length;
  return (
    <div data-testid="tracking-https-job" className="border border-zinc-800 rounded-sm p-3 space-y-2">
      <div className="text-xs mono flex items-center gap-2">
        {job.status === "running" ? <Loader2 size={12} className="animate-spin text-amber-300" /> : <CheckCircle2 size={12} className="text-emerald-400" />}
        <span data-testid="tracking-https-progress" className={job.status === "running" ? "text-amber-300" : "text-emerald-300"}>
          {job.status === "running" ? `Upgrading ${job.done}/${job.total}…` : `Done · ${okCount}/${job.total} HTTPS ready now`}
        </span>
        {job.status !== "running" && okCount < job.total && <span className="text-zinc-500">— pending certs are re-checked by the 10-min sweep</span>}
      </div>
      {results.length > 0 && <ul data-testid="tracking-https-results" className="space-y-1">{results.map((r) => <ResultLine key={r.site_id} r={r} />)}</ul>}
    </div>
  );
}

/** Settings → Email tracking links: one-click network upgrade of click.<domain> to v2.spgo.io + managed HTTPS. */
export default function TrackingHttpsPanel() {
  const s = useTrackingHttps();
  const p = s.plan;
  const pending = (p?.targets || []).filter((t) => !t.https_ok);
  const nothing = p && p.eligible === 0;
  return (
    <div data-testid="tracking-https-panel" className="space-y-3">
      <div className="text-xs text-zinc-400">
        Email links are wrapped with <span className="mono text-zinc-200">click.&lt;domain&gt;</span>. That host must CNAME to <span className="mono text-zinc-200">{p?.cname_target || "v2.spgo.io"}</span> with SparkPost&apos;s managed certificate and be linked to the sending domain — the legacy <span className="mono">spgo.io</span> target breaks https links (SSL error / 404). New connections get this automatically; this button upgrades every site connected earlier.
      </div>
      <div data-testid="tracking-https-summary" className="text-[11px] text-zinc-500 mono">
        {p ? `${p.count} SparkPost-connected site(s) · ${p.already_ok} HTTPS ready · ${p.count - p.already_ok} to upgrade` : "…"}
      </div>
      {pending.length > 0 && !s.job && (
        <ul data-testid="tracking-https-pending" className="space-y-1">{pending.map((t) => <SiteLine key={t.site_id} t={t} />)}</ul>
      )}
      <div className="flex items-center gap-2">
        <button type="button" data-testid="tracking-https-run-btn" disabled={s.busy || s.running || nothing || !p} onClick={s.run}
          className="px-3 py-1.5 text-xs font-semibold bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-40 rounded-sm inline-flex items-center gap-1.5">
          {s.busy || s.running ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />}
          {nothing ? "No connected sites" : "Upgrade tracking to HTTPS (all sites)"}
        </button>
        <button type="button" data-testid="tracking-https-refresh-btn" onClick={s.refresh} disabled={s.running} title="Refresh" className="text-zinc-400 hover:text-zinc-100 px-2 py-1.5 rounded-sm text-xs disabled:opacity-50"><RefreshCw size={12} /></button>
      </div>
      {s.job && <JobView job={s.job} />}
      {s.err && <div data-testid="tracking-https-error" className="text-xs text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm font-mono">{s.err}</div>}
    </div>
  );
}
